import { Injectable, inject } from '@angular/core';
import { IControlModel } from '../model/control.model';
import { ENV_CONFIG, EnvironmentConfig, Page, PageResponse, PageUtil } from '@libs/vc-core-lib';
import { Observable, forkJoin, map } from 'rxjs';
import { IControlSearchDTO } from '../dto/control.search.dto';
import { IControlHierarchyHolderModel } from '../model/control-hierarchy-holder.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IControlCriteriaDTO } from '../dto/control.criteria.dto';
import { IBulkUploadActionResult } from '../model/bulk-upload-action-result';

@Injectable({
    providedIn: 'root',
})
export class ControlService {
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private readonly _controlPath = 'control';

    public get(id: string): Observable<IControlModel> {
        return this._httpClient.get<IControlModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${id}`
        );
    }

    public save(control: IControlModel): Observable<IControlModel> {
        return this._httpClient.post<IControlModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}`,
            control
        );
    }

    public update(control: IControlModel): Observable<IControlModel> {
        return this._httpClient.put<IControlModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${control.id}`,
            control
        );
    }

    // NOTE: Implement endpoint for deleting multiple controls (MCS-1602)
    public delete(controlModels: IControlModel[]): Observable<void[]> {
        const calls = controlModels.map((controlModel) =>
            this._httpClient.delete<void>(
                `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${controlModel.id}`
            )
        );
        return forkJoin(calls);
    }

    public pageSearch(searchDTO: IControlSearchDTO): Observable<Page<IControlModel>> {
        return this._httpClient
            .post<
                PageResponse<IControlModel>
            >(`${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/page-search`, searchDTO)
            .pipe(map((response: PageResponse<IControlModel>) => PageUtil.convertToPage(response)));
    }

    public search(searchDTO: IControlCriteriaDTO): Observable<IControlModel[]> {
        return this._httpClient.post<IControlModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/search`,
            searchDTO
        );
    }

    public list(ids: number[]): Observable<IControlModel[]> {
        return this._httpClient.post<IControlModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/list`,
            ids
        );
    }

    public getHierarchyView(id: number): Observable<IControlHierarchyHolderModel> {
        return this._httpClient.get<IControlHierarchyHolderModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${id}/hierarchy-view`
        );
    }

    public setParent(id: number, parentId: number): Observable<void> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        return this._httpClient.put<void>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${id}/parent`,
            parentId,
            { headers }
        );
    }

    public removeParent(id: number): Observable<void> {
        return this._httpClient.delete<void>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/${id}/parent`
        );
    }

    public downloadEmptyTemplate(): Observable<Blob> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/bulk-template`;
        return this._httpClient.get(url, { responseType: 'blob' });
    }

    public downloadFilledTemplate(controlIds: number[]): Observable<Blob> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/bulk-template`;
        return this._httpClient.post(url, controlIds, { responseType: 'blob' });
    }

    public uploadBulkTemplate(file: File): Observable<IBulkUploadActionResult> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._controlPath}/upload/bulk-template`;
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<IBulkUploadActionResult>(url, formData);
    }
}
